import React, { useState, useEffect } from 'react';
import Box from '@mui/joy/Box';
import Typography from '@mui/joy/Typography';
import Button from '@mui/joy/Button';
import Modal from '@mui/joy/Modal';
import ModalDialog from '@mui/joy/ModalDialog';
import ModalClose from '@mui/joy/ModalClose';
import Checkbox from '@mui/joy/Checkbox';
import Alert from '@mui/joy/Alert';
import Divider from '@mui/joy/Divider';

import LanguageIcon from '@mui/icons-material/Language';
import CheckIcon from '@mui/icons-material/Check';

import { themeColors, shadows } from '../../styles/theme';

export default function LanguageSelectionModal({
  open,
  onClose,
  supportedLanguages = [],
  selectedLanguages = [],
  onSave,
}) {
  const [localSelection, setLocalSelection] = useState({});
  const [error, setError] = useState('');

  // Initialize selection from selectedLanguages when modal opens
  useEffect(() => {
    if (open && supportedLanguages.length > 0) {
      const selection = {};
      supportedLanguages.forEach((lang) => {
        // Check if this language is in selectedLanguages
        // API returns: [{id: 1, language: {id: 5, name: "English", code: "en"}}, ...]
        const isSelected = selectedLanguages.some((selected) => {
          if (typeof selected === 'number') {
            // selectedLanguages is array of IDs
            return selected === lang.id;
          } else if (typeof selected === 'object' && selected !== null) {
            // selectedLanguages is array of objects with nested language
            if (selected.language) {
              return selected.language.id === lang.id || selected.language.name === lang.name;
            }
            // Fallback for other formats
            return selected.id === lang.id || selected.name === lang.name;
          }
          return false;
        });
        selection[lang.name] = isSelected;
      });
      setLocalSelection(selection);
      setError('');
    }
  }, [open, supportedLanguages, selectedLanguages]);

  const handleToggle = (languageName) => {
    const currentCount = Object.values(localSelection).filter(Boolean).length;

    // Prevent deselecting the last language
    if (localSelection[languageName] && currentCount === 1) {
      setError('At least one language must be selected');
      return;
    }

    setError('');
    setLocalSelection((prev) => ({
      ...prev,
      [languageName]: !prev[languageName],
    }));
  };

  const handleSave = () => {
    const selectedCount = Object.values(localSelection).filter(Boolean).length;

    if (selectedCount === 0) {
      setError('Please select at least one language');
      return;
    }

    // Get IDs of selected languages
    const selectedIds = Object.entries(localSelection)
      .filter(([name, isSelected]) => isSelected)
      .map(([name]) => supportedLanguages.find((lang) => lang.name === name)?.id)
      .filter(Boolean);

    onSave(selectedIds);
    onClose();
  };

  const selectedCount = Object.values(localSelection).filter(Boolean).length;

  return (
    <Modal open={open} onClose={onClose}>
      <ModalDialog
        sx={{
          width: 500,
          maxHeight: '80vh',
          border: 'none',
          boxShadow: shadows.xl,
          borderRadius: 2.5,
          p: 0,
        }}
      >
        {/* Header */}
        <Box
          sx={{
            p: 3,
            pb: 2.5,
            borderBottom: `1px solid ${themeColors.neutral[100]}`,
          }}
        >
          <ModalClose
            sx={{
              top: 16,
              right: 16,
              width: 32,
              height: 32,
              backgroundColor: themeColors.neutral[100],
              border: 'none',
              '&:hover': {
                backgroundColor: themeColors.neutral[200],
              },
            }}
          />

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
            <Box
              sx={{
                width: 40,
                height: 40,
                borderRadius: 1.75,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                background: `linear-gradient(135deg, ${themeColors.primary[500]}, ${themeColors.primary[600]})`,
                boxShadow: `0 4px 12px ${themeColors.primary[500]}30`,
              }}
            >
              <LanguageIcon sx={{ fontSize: 22, color: 'white' }} />
            </Box>

            <Box>
              <Typography level="h4" sx={{ fontWeight: 700, color: themeColors.neutral[900] }}>
                Transcript Languages
              </Typography>
              <Typography level="body-sm" sx={{ color: themeColors.neutral[600], fontSize: '0.8125rem' }}>
                {selectedCount} of {supportedLanguages.length} selected
              </Typography>
            </Box>
          </Box>
        </Box>

        {/* Content */}
        <Box sx={{ p: 3, overflow: 'auto', maxHeight: '50vh' }}>
          {error && (
            <Alert
              color="danger"
              sx={{
                mb: 2,
                borderRadius: 1.5,
                border: 'none',
                backgroundColor: `${themeColors.danger}10`,
                fontSize: '0.8125rem',
              }}
            >
              {error}
            </Alert>
          )}

          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
            {supportedLanguages.map((lang) => (
              <Box
                key={lang.id}
                onClick={() => handleToggle(lang.name)}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1.5,
                  p: 1.5,
                  borderRadius: 1.5,
                  cursor: 'pointer',
                  transition: 'all 0.15s ease',
                  backgroundColor: localSelection[lang.name] ? `${themeColors.primary[500]}08` : 'transparent',
                  border: `1px solid ${localSelection[lang.name] ? themeColors.primary[300] : themeColors.neutral[100]}`,
                  '&:hover': {
                    backgroundColor: localSelection[lang.name]
                      ? `${themeColors.primary[500]}12`
                      : themeColors.neutral[50],
                    borderColor: localSelection[lang.name]
                      ? themeColors.primary[400]
                      : themeColors.neutral[200],
                  },
                }}
              >
                <Box
                  sx={{
                    width: 20,
                    height: 20,
                    borderRadius: 0.75,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    border: `2px solid ${localSelection[lang.name] ? themeColors.primary[500] : themeColors.neutral[300]}`,
                    backgroundColor: localSelection[lang.name] ? themeColors.primary[500] : 'white',
                    transition: 'all 0.15s ease',
                  }}
                >
                  {localSelection[lang.name] && (
                    <CheckIcon sx={{ fontSize: 14, color: 'white' }} />
                  )}
                </Box>

                <Typography
                  level="body-sm"
                  sx={{
                    fontWeight: localSelection[lang.name] ? 600 : 500,
                    color: localSelection[lang.name] ? themeColors.neutral[900] : themeColors.neutral[700],
                    fontSize: '0.875rem',
                    flex: 1,
                  }}
                >
                  {lang.name}
                </Typography>

                {lang.code && (
                  <Typography
                    level="body-xs"
                    sx={{
                      color: themeColors.neutral[500],
                      fontFamily: 'monospace',
                      fontSize: '0.75rem',
                    }}
                  >
                    {lang.code}
                  </Typography>
                )}
              </Box>
            ))}
          </Box>
        </Box>

        {/* Footer */}
        <Box
          sx={{
            p: 3,
            pt: 2.5,
            borderTop: `1px solid ${themeColors.neutral[100]}`,
            display: 'flex',
            gap: 1.5,
            justifyContent: 'flex-end',
          }}
        >
          <Button
            variant="plain"
            onClick={onClose}
            sx={{
              color: themeColors.neutral[700],
              fontWeight: 500,
              fontSize: '0.875rem',
              '&:hover': {
                backgroundColor: themeColors.neutral[100],
              },
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSave}
            disabled={selectedCount === 0}
            sx={{
              background: `linear-gradient(135deg, ${themeColors.primary[500]}, ${themeColors.primary[600]})`,
              boxShadow: `0 2px 8px ${themeColors.primary[500]}30`,
              fontWeight: 600,
              fontSize: '0.875rem',
              px: 3,
              '&:hover': {
                boxShadow: `0 4px 12px ${themeColors.primary[500]}40`,
              },
              '&:disabled': {
                opacity: 0.5,
              },
            }}
          >
            Save Selection
          </Button>
        </Box>
      </ModalDialog>
    </Modal>
  );
}
